import { Component, OnInit, OnDestroy, ViewChild, ElementRef, ChangeDetectionStrategy, ChangeDetectorRef, Inject, PLATFORM_ID, HostListener } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Subscription } from 'rxjs';
import { TourService, TourState, TourStep, NavigationOption, Tour } from './tour.service';
import { LayoutService } from '../layout/layout.service';
import { AuthService } from '../../services/auth.service';
import { AlertService } from '../../services/alert.service';
import { DemoPopoverPositionService } from './demo-popover-position.service';
import { PopoverPosition, PopoverArrowPosition } from './tour.types';

@Component({
  selector: 'app-tour',
  templateUrl: './tour.component.html',
  styleUrls: ['./tour.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TourComponent implements OnInit, OnDestroy {
  tourState: TourState | null = null;
  progressPercentage: number = 0;
  availableTours: Tour[] = [];
  completedTours: Map<string, boolean> = new Map();
  isBrowser: boolean;
  popoverPosition: PopoverPosition = {};
  arrowPosition: PopoverArrowPosition | null = null;
  isExecutingActions: boolean = false;
  isPositioningPopover: boolean = false;
  showLoader: boolean = false;
  private loaderTimeout: any = null;
  
  @ViewChild('videoPlayer') videoPlayer!: ElementRef<HTMLVideoElement>;
  currentPlaybackSpeed: number = 1;
  
  private subscription: Subscription = new Subscription();

  constructor(
    @Inject(PLATFORM_ID) platformId: Object,
    public tourService: TourService,
    public layoutService: LayoutService,
    private authService: AuthService,
    private alertService: AlertService,
    private cdr: ChangeDetectorRef,
    private positionService: DemoPopoverPositionService
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    // Get available tours filtered by staff status
    const allTours = this.tourService.getAllTours();
    const isStaff = this.authService.userObject?.axipStaff === true;
    
    // Filter tours: show all tours to staff, only non-staff tours to regular users
    this.availableTours = allTours.filter(tour => {
      if (tour.staffOnly) {
        return isStaff;
      }
      return true;
    });
    
    console.log('[TourComponent] Available tours:', this.availableTours.length, 'isStaff:', isStaff);
    
    // Load completed tours
    this.loadCompletedTours();
    
    console.log('[TourComponent] ngOnInit - subscribing to tour state');
    this.subscription.add(
      this.tourService.tourState$.subscribe(state => {
        console.log('[TourComponent] Received state update:', {
          isActive: state.isActive,
          stepIndex: state.stepIndex,
          stepId: state.currentStep?.id
        });
        this.tourState = state;
        this.calculateProgress();
        
        // Update popover position when step changes
        if (state.isActive && state.currentStep) {
          const delay = state.currentStep.positionPopoverDelay || 0;
          if (delay > 0) {
            this.isPositioningPopover = true;
            this.cdr.markForCheck();
            setTimeout(() => {
              this.updatePopoverPosition();
              this.isPositioningPopover = false;
              this.cdr.markForCheck();
            }, delay);
          } else {
            this.updatePopoverPosition();
          }
        }
        
        // Reload completed tours when returning to selection screen
        if (!state.isActive) {
          this.loadCompletedTours();
        }
        
        this.cdr.markForCheck();
      })
    );
  }

  /**
   * Update popover position based on current step configuration
   */
  private updatePopoverPosition(): void {
    if (!this.tourState?.currentStep) return;

    const step = this.tourState.currentStep;
    const result = this.positionService.calculatePosition(
      step.targetSelector,
      step.attachment || 'auto',
      step.defaultCorner || 'bottom-right',
      step.alignment || 'center'
    );

    const constrained = this.positionService.constrainToViewport(result);
    this.popoverPosition = constrained.position;
    this.arrowPosition = constrained.arrow || null;
    this.cdr.markForCheck();
  }

  /**
   * Get CSS classes for the arrow
   */
  getArrowClasses(): string {
    if (!this.arrowPosition) return '';
    return `has-arrow arrow-${this.arrowPosition.side}`;
  }

  /**
   * Get CSS custom properties for arrow positioning
   */
  getArrowStyle(): { [key: string]: string } {
    if (!this.arrowPosition?.offsetPercent) return {};
    return {
      '--arrow-offset': `${this.arrowPosition.offsetPercent}%`
    };
  }

  /**
   * Recalculate position on window resize or scroll
   */
  @HostListener('window:resize')
  @HostListener('window:scroll')
  onWindowChange(): void {
    if (this.tourState?.isActive && this.tourState?.currentStep) {
      this.updatePopoverPosition();
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    if (this.loaderTimeout) {
      clearTimeout(this.loaderTimeout);
    }
  }

  /**
   * Load completed tours from Firebase
   */
  private async loadCompletedTours(): Promise<void> {
    try {
      const completed = await this.tourService.getCompletedTours();
      this.completedTours = new Map(completed.map(tourId => [tourId, true]));
      this.cdr.markForCheck(); // Trigger change detection
    } catch (error) {
      console.error('Error loading completed tours:', error);
    }
  }

  /**
   * Check if a tour is completed
   */
  isTourCompleted(tourId: string): boolean {
    return this.completedTours.has(tourId);
  }

  /**
   * Close the detail panel
   */
  closePanel(): void {
    this.layoutService.toggleRightPanel();
  }

  /**
   * Calculate progress percentage based on completed steps
   */
  private calculateProgress(): void {
    if (!this.tourState || !this.tourState.tour) {
      this.progressPercentage = 0;
      return;
    }

    const totalSteps = this.getVisibleSteps().length;
    const currentIndex = this.tourState.stepIndex;
    
    if (totalSteps === 0) {
      this.progressPercentage = 0;
    } else {
      this.progressPercentage = ((currentIndex + 1) / totalSteps) * 100;
    }
  }

  /**
   * Get list of visible steps (those that meet conditions)
   */
  private getVisibleSteps(): TourStep[] {
    if (!this.tourState?.tour || !this.tourState.metadata) return [];
    
    return this.tourState.tour.steps.filter(step => {
      if (!step.condition) return true;
      return step.condition(this.tourState!.metadata!);
    });
  }

  /**
   * Get progress text (e.g., "Step 3 of 8")
   */
  getProgressText(): string {
    if (!this.tourState || !this.tourState.tour) return '';
    
    const visibleSteps = this.getVisibleSteps();
    const totalSteps = visibleSteps.length;
    const currentStep = this.tourState.stepIndex + 1;
    
    return `Step ${currentStep} of ${totalSteps}`;
  }

  /**
   * Handle next button click
   */
  async onNext(): Promise<void> {
    this.isExecutingActions = true;
    
    // Debounce loader - only show if action takes more than 200ms
    this.loaderTimeout = setTimeout(() => {
      this.showLoader = true;
      this.cdr.markForCheck();
    }, 200);
    
    try {
      await this.tourService.nextStep();
    } finally {
      clearTimeout(this.loaderTimeout);
      this.showLoader = false;
      this.isExecutingActions = false;
      this.cdr.markForCheck();
    }
  }

  /**
   * Handle previous button click
   */
  async onPrevious(): Promise<void> {
    this.isExecutingActions = true;
    
    // Debounce loader - only show if action takes more than 200ms
    this.loaderTimeout = setTimeout(() => {
      this.showLoader = true;
      this.cdr.markForCheck();
    }, 200);
    
    try {
      await this.tourService.previousStep();
    } finally {
      clearTimeout(this.loaderTimeout);
      this.showLoader = false;
      this.isExecutingActions = false;
      this.cdr.markForCheck();
    }
  }

  /**
   * Handle option selection in form-select navigation
   */
  async onSelectOption(optionId: string): Promise<void> {
    this.isExecutingActions = true;
    
    // Debounce loader - only show if action takes more than 200ms
    this.loaderTimeout = setTimeout(() => {
      this.showLoader = true;
      this.cdr.markForCheck();
    }, 200);
    
    try {
      await this.tourService.selectOption(optionId);
    } finally {
      clearTimeout(this.loaderTimeout);
      this.showLoader = false;
      this.isExecutingActions = false;
      this.cdr.markForCheck();
    }
  }

  /**
   * Handle close button click
   */
  async onClose(): Promise<void> {
    const result = await this.alertService.open({
      heading: 'Close Tour?',
      description: 'Your progress will be saved. You can resume this tour later.',
      buttons: [
        {
          label: 'Cancel',
          color: 'neutral',
          icon: 'fa-solid fa-times'
        },
        {
          label: 'Close Tour',
          color: 'danger',
          icon: 'fa-solid fa-check'
        }
      ]
    });

    if (result.button?.label === 'Close Tour') {
      this.tourService.closeTour();
    }
  }

  /**
   * Handle reset button click - delete completion record
   */
  async onReset(): Promise<void> {
    const tourId = this.tourState?.tour?.id;
    if (!tourId) return;

    const result = await this.alertService.open({
      heading: 'Reset Tour Progress?',
      description: 'This will delete your completion record for this tour. This action cannot be undone.',
      buttons: [
        {
          label: 'Cancel',
          color: 'neutral',
          icon: 'fa-solid fa-times'
        },
        {
          label: 'Reset Progress',
          color: 'danger',
          icon: 'fa-solid fa-trash'
        }
      ]
    });

    if (result.button?.label === 'Reset Progress') {
      await this.tourService.deleteTourCompletion(tourId);
      this.completedTours.delete(tourId);
      this.cdr.markForCheck();
      this.tourService.closeTour();
    }
  }

  /**
   * Start a tour from the selection screen
   */
  async startTour(tourId: string): Promise<void> {
    this.isExecutingActions = true;
    
    // Debounce loader - only show if action takes more than 200ms
    this.loaderTimeout = setTimeout(() => {
      this.showLoader = true;
      this.cdr.markForCheck();
    }, 200);
    
    try {
      await this.tourService.startTour(tourId);
    } finally {
      clearTimeout(this.loaderTimeout);
      this.showLoader = false;
      this.isExecutingActions = false;
      this.cdr.markForCheck();
    }
  }

  /**
   * Handle pause button click
   */
  onPause(): void {
    this.tourService.pauseTour();
  }

  /**
   * Check if previous button should be enabled
   */
  isPreviousEnabled(): boolean {
    if (!this.tourState?.currentStep) return false;
    return this.tourState.currentStep.navigation.previousEnabled;
  }

  /**
   * Check if navigation is button-based
   */
  isButtonNavigation(): boolean {
    return this.tourState?.currentStep?.navigation.type === 'buttons';
  }

  /**
   * Check if navigation is form-select based
   */
  isFormSelectNavigation(): boolean {
    return this.tourState?.currentStep?.navigation.type === 'form-select';
  }

  /**
   * Get options for form-select navigation
   */
  getNavigationOptions(): NavigationOption[] {
    if (!this.tourState?.currentStep?.navigation.options) return [];
    return this.tourState.currentStep.navigation.options;
  }

  /**
   * Check if this is the last step
   */
  isLastStep(): boolean {
    if (!this.tourState || !this.tourState.tour) return false;
    const visibleSteps = this.getVisibleSteps();
    return this.tourState.stepIndex >= visibleSteps.length - 1;
  }

  /**
   * Video player controls
   */
  onVideoMetadataLoaded(): void {
    this.setVideoPlaybackSpeed();
  }

  setVideoPlaybackSpeed(): void {
    if (this.videoPlayer && this.videoPlayer.nativeElement) {
      this.videoPlayer.nativeElement.playbackRate = this.currentPlaybackSpeed;
    }
  }

  changeSpeed(delta: number): void {
    let newSpeed = this.currentPlaybackSpeed + delta;
    if (newSpeed < 0.25) {
      newSpeed = 0.25;
    } else if (newSpeed > 4) {
      newSpeed = 4;
    }
    this.currentPlaybackSpeed = parseFloat(newSpeed.toFixed(2));
    this.setVideoPlaybackSpeed();
  }
}
